'use client';
/**
 * Guest-first account access. Email/password has explicit sign-in and registration modes; creating
 * an account while anonymous lets Better Auth's anonymous plugin move the current albums into it.
 * Provider capability comes from GET /api/me, so the UI never advertises a disabled method.
 */
import * as React from 'react';
import { LogIn, ShieldCheck, UserPlus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { MeResponse } from '@freealbum/shared';
import { Button, Dialog, Input, SegmentedControl, Separator, toast } from '@freealbum/ui';
import { authClient } from '@/auth/client';
import { Link } from '@/i18n/navigation';
import { LinkIcon } from '@/components/ui/link-status';
import { AlbumLoader } from '@/components/ui/album-loader';

export type Features = MeResponse['features'];
export type AuthMode = 'signIn' | 'register';

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function GoogleGlyph() {
  return (
    <svg aria-hidden viewBox="0 0 48 48" className="size-4">
      <path fill="#4285F4" d="M45 24c0-1.6-.1-2.7-.4-4H24v7.5h12c-.2 2-1.5 5-4.4 7l6.7 5.2C42.2 36 45 30.6 45 24Z" />
      <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.3l-6.9-5.4C29.7 36.6 27.1 37.5 24 37.5c-5.8 0-10.7-3.9-12.5-9.1l-7.1 5.5C8 41.4 15.4 46 24 46Z" />
      <path fill="#FBBC05" d="M11.5 28.4A13.5 13.5 0 0 1 10.8 24c0-1.5.3-3 .7-4.4l-7.1-5.5A22 22 0 0 0 2 24c0 3.5.8 6.9 2.4 9.9l7.1-5.5Z" />
      <path fill="#EA4335" d="M24 10.5c4.1 0 6.9 1.8 8.5 3.3l6.2-6C34.9 4.3 29.9 2 24 2 15.4 2 8 6.6 4.4 14.1l7.1 5.5C13.3 14.4 18.2 10.5 24 10.5Z" />
    </svg>
  );
}

function authErrorCode(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const candidate = error as { code?: unknown; message?: unknown };
  return String(candidate.code ?? candidate.message ?? '').toUpperCase();
}

export interface SignInProps {
  features: Features | null;
  onSignedIn?: () => void | Promise<unknown>;
  callbackURL?: string;
  mode?: AuthMode;
  initialMode?: AuthMode;
  onModeChange?: (mode: AuthMode) => void;
}

export function SignInForm({
  features,
  onSignedIn,
  callbackURL = '/albums',
  mode: controlledMode,
  initialMode = 'signIn',
  onModeChange,
}: SignInProps) {
  const t = useTranslations('auth');
  const tc = useTranslations('common');
  const [localMode, setLocalMode] = React.useState<AuthMode>(initialMode);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [formError, setFormError] = React.useState('');

  const hasGoogle = features?.google ?? false;
  // devLogin fallback keeps a cached pre-registration /api/me response usable until refresh.
  const hasEmail = features?.emailPassword ?? features?.devLogin ?? false;
  const canRegister = features?.emailRegistration ?? false;
  const requestedMode = controlledMode ?? localMode;
  const mode: AuthMode = hasEmail && canRegister ? requestedMode : 'signIn';

  const changeMode = (next: AuthMode) => {
    if (busy) return;
    setLocalMode(next);
    setErrors({});
    setFormError('');
    onModeChange?.(next);
  };

  const clearError = (field: keyof FormErrors) => {
    if (errors[field]) setErrors((current) => ({ ...current, [field]: undefined }));
    if (formError) setFormError('');
  };

  const google = async () => {
    setBusy(true);
    setFormError('');
    try {
      const result = await authClient.signIn.social({ provider: 'google', callbackURL });
      if (result?.error) throw result.error;
    } catch {
      setFormError(t('signInFailed'));
      toast({ title: t('signInFailed'), variant: 'error' });
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: FormErrors = {};
    const cleanEmail = email.trim();
    const cleanName = name.trim();

    if (!cleanEmail) nextErrors.email = t('emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) nextErrors.email = t('emailInvalid');
    if (!password) nextErrors.password = t('passwordRequired');
    else if (password.length < 8) nextErrors.password = t('passwordTooShort');
    if (mode === 'register') {
      if (!cleanName) nextErrors.name = t('nameRequired');
      if (!confirmPassword) nextErrors.confirmPassword = t('confirmPasswordRequired');
      else if (password !== confirmPassword) nextErrors.confirmPassword = t('passwordMismatch');
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFormError('');
      return;
    }

    setBusy(true);
    setErrors({});
    setFormError('');
    try {
      const result = mode === 'register'
        ? await authClient.signUp.email({ email: cleanEmail, password, name: cleanName, callbackURL })
        : await authClient.signIn.email({ email: cleanEmail, password, callbackURL });
      if (result?.error) throw result.error;

      // Keep the form busy while its owner closes or navigates, so controls cannot be submitted
      // twice under the transition.
      await onSignedIn?.();
    } catch (error) {
      const code = authErrorCode(error);
      if (/USER_ALREADY_EXISTS/.test(code)) {
        setErrors({ email: t('emailAlreadyUsed') });
      } else if (/INVALID_EMAIL/.test(code)) {
        setErrors({ email: t('emailInvalid') });
      } else if (/INVALID_EMAIL_OR_PASSWORD|CREDENTIAL_ACCOUNT_NOT_FOUND/.test(code)) {
        setFormError(t('invalidCredentials'));
      } else if (/PASSWORD_TOO_SHORT/.test(code)) {
        setErrors({ password: t('passwordTooShort') });
      } else {
        setFormError(mode === 'register' ? t('registrationFailed') : t('signInFailed'));
      }
      setBusy(false);
    }
  };

  if (features == null) return <AlbumLoader label={t('signInChecking')} size="compact" />;

  if (!hasGoogle && !hasEmail) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium text-ink">{t('signInUnavailable')}</p>
        <p className="text-sm text-ink-2">{t('signInUnavailableBody')}</p>
        <Button variant="secondary" size="lg" block asChild startIcon={<LinkIcon><ShieldCheck aria-hidden /></LinkIcon>}>
          <Link href="/albums">{t('guestBackupCta')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-2">{mode === 'register' ? t('registerLead') : t('signInLead')}</p>

      {canRegister ? (
        <SegmentedControl<AuthMode>
          aria-label={t('accountAccess')}
          value={mode}
          onValueChange={changeMode}
          fullWidth
          options={[
            { value: 'signIn', label: t('signIn'), disabled: busy },
            { value: 'register', label: t('createAccount'), disabled: busy },
          ]}
        />
      ) : null}

      {hasGoogle ? (
        <Button variant="secondary" size="lg" block onClick={() => void google()} loading={busy} startIcon={<GoogleGlyph />}>
          {mode === 'register' ? t('continueWithGoogle') : t('signInWithGoogle')}
        </Button>
      ) : null}

      {hasGoogle && hasEmail ? <Separator label={tc('or')} /> : null}

      {hasEmail ? (
        <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3" noValidate>
          {mode === 'register' ? (
            <Input
              label={t('name')}
              autoComplete="name"
              required
              maxLength={80}
              value={name}
              placeholder={t('namePlaceholder')}
              error={errors.name}
              disabled={busy}
              onChange={(event) => {
                setName(event.target.value);
                clearError('name');
              }}
            />
          ) : null}
          <Input
            label={t('email')}
            type="email"
            autoComplete="email"
            required
            value={email}
            placeholder={t('emailPlaceholder')}
            error={errors.email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              clearError('email');
            }}
            dir="ltr"
          />
          <Input
            label={t('password')}
            type="password"
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            required
            minLength={8}
            maxLength={128}
            value={password}
            hint={mode === 'register' ? t('passwordHint') : undefined}
            error={errors.password}
            disabled={busy}
            onChange={(event) => {
              setPassword(event.target.value);
              clearError('password');
              clearError('confirmPassword');
            }}
            dir="ltr"
          />
          {mode === 'register' ? (
            <Input
              label={t('confirmPassword')}
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              maxLength={128}
              value={confirmPassword}
              error={errors.confirmPassword}
              disabled={busy}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
                clearError('confirmPassword');
              }}
              dir="ltr"
            />
          ) : null}

          {formError ? <p role="alert" className="text-sm text-danger">{formError}</p> : null}

          <Button
            type="submit"
            variant="primary"
            block
            loading={busy}
            startIcon={mode === 'register' ? <UserPlus aria-hidden /> : <LogIn aria-hidden />}
          >
            {mode === 'register' ? t('createAccount') : t('signIn')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}

export function SignInDialog({
  open,
  onOpenChange,
  features,
  initialMode = 'signIn',
  onSignedIn,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  features: Features | null;
  initialMode?: AuthMode;
  onSignedIn?: () => void | Promise<unknown>;
}) {
  const t = useTranslations('auth');
  const [mode, setMode] = React.useState<AuthMode>(initialMode);

  React.useEffect(() => {
    if (open) setMode(initialMode);
  }, [initialMode, open]);

  const complete = async () => {
    onOpenChange(false);
    await onSignedIn?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={mode === 'register' ? t('createAccountTitle') : t('signInTitle')} size="sm">
      <SignInForm features={features} mode={mode} onModeChange={setMode} onSignedIn={complete} />
    </Dialog>
  );
}
